import { useEffect, useState } from 'react';
import { ArrowUpRight, CheckCircle2, Circle } from 'lucide-react';
import { api, type SetupView } from '../api';
import { Button } from './ui/button';

/**
 * The setup checklist on an owner's home page: each step with its live status from the server, a link to where it is
 * done, and the two marks the checklist itself records (policy reviewed, go-live requested). Requesting go-live asks
 * Alder to review the setup; it does not turn on sending. Hidden once every step is done, and for non-owners.
 */
export function SetupChecklist({ owner }: { owner: boolean }) {
  const [view, setView] = useState<SetupView | null>(null);
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  useEffect(() => {
    if (!owner) return;
    let active = true;
    void api<SetupView>('/app-api/setup').then(v => { if (active) setView(v); }).catch(() => {});
    return () => { active = false; };
  }, [owner]);
  if (!owner || !view || view.complete) return null;

  async function record(path: string) {
    setBusy(true); setError('');
    try { setView(await api<SetupView>(path, {})); }
    catch (e) { setError(e instanceof Error ? e.message : 'This step could not be recorded.'); }
    finally { setBusy(false); }
  }

  const next = view.steps.find(step => !step.done);
  // A slim bar with progress and the next step; the full list opens on demand, so setup never crowds out the work.
  return <section className="home-setup" aria-labelledby="home-setup-title">
    <details>
      <summary>
        <span className="home-setup-progress" aria-hidden><span style={{ width: `${Math.round(view.done / view.total * 100)}%` }}/></span>
        <h2 id="home-setup-title" className="home-setup-title">Set up your workspace</h2>
        <span className="home-setup-count" role="status">{view.done} of {view.total} done</span>
        {next && <span className="home-setup-next">Next: {next.title}</span>}
        <span className="home-setup-toggle">Show steps</span>
      </summary>
    <ol className="home-setup-steps">{view.steps.map(step => <li key={step.id} className={step.done ? 'done' : undefined}
      data-step={step.id}>
      {step.done ? <CheckCircle2 size={18} aria-hidden className="home-setup-icon" /> : <Circle size={18} aria-hidden className="home-setup-icon" />}
      <div className="home-setup-body">
        <strong>{step.title}<span className="sr-only">{step.done ? ' (done)' : ' (not done)'}</span></strong>
        <small>{step.detail}</small>
      </div>
      <div className="home-setup-action">
        {step.id === 'policy' && !step.done && <Button variant="outline" size="sm" disabled={busy}
          onClick={() => void record('/app-api/setup/policy-reviewed')}>Mark reviewed</Button>}
        {step.id === 'go_live' && (step.done ? <span className="home-setup-requested">Requested</span>
          : <Button size="sm" disabled={busy || !view.canRequestGoLive}
            onClick={() => void record('/app-api/setup/go-live')}>Request go-live</Button>)}
        {step.href && !step.done && <a href={step.href} aria-label={`${step.title}: open`}>Open<ArrowUpRight size={14} aria-hidden /></a>}
      </div>
    </li>)}</ol>
    </details>
    {error && <p className="alert error" role="alert">{error}</p>}
  </section>;
}
